import { FunctionComponent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { faPlus, faSlidersH, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { SCORING_SWATCHES } from "../../shell/ScoreMatrix/matrixModel";

import { OverviewStats } from "./utils/overviewStats";

interface OverviewSummaryProps {
	stats: OverviewStats;
	onCreateScoring: () => void;
	onEditScoring: (criteriaId: number) => void;
}

/**
 * The "Lead Scoring Summary" box under the matrix (PREC-4055): the counts in
 * prose, and one usage line per lead scoring with a bar and an edit shortcut.
 *
 * The edit shortcut is laid out at all times and only hidden (PREC-4079):
 * toggling `display` on hover took the button out of the row and let it back
 * in, so every row grew by the button's height as the pointer passed over it.
 * `w-fit` keeps it at its label's width — a grid item stretches to its column
 * otherwise, which is what made it run to the edge of the box.
 */
export const OverviewSummary: FunctionComponent<OverviewSummaryProps> = ({
	stats,
	onCreateScoring,
	onEditScoring,
}) => {
	const { t } = useTranslation();
	const bullet =
		"relative pl-4 text-xs text-text-meta before:absolute before:left-0.5 before:top-2 before:h-1 before:w-1 before:rounded-full before:bg-text-disabled";

	return (
		<div
			className="rounded-xl border border-border-light bg-white p-4"
			data-testid="overview-summary"
		>
			<h3 className="mb-2 text-sm font-semibold text-text">
				{t("leadScoring.overview.summaryTitle")}
			</h3>
			<ul className="flex flex-col gap-1.5">
				<li className={bullet}>
					<Trans
						components={{ b: <b className="font-semibold text-text" /> }}
						i18nKey="leadScoring.overview.combinations"
						values={{ segments: stats.segments, personas: stats.personas, live: stats.live }}
					/>
				</li>
				{stats.off > 0 && (
					<li className={bullet}>
						<Trans
							components={{ b: <b className="font-semibold text-text" /> }}
							i18nKey="leadScoring.overview.deactivatedCombinations"
							values={{ count: stats.off }}
						/>
					</li>
				)}
				<li className={bullet}>{t("leadScoring.overview.everyCombination")}</li>
				<li className={bullet}>{t("leadScoring.overview.separateScoring")}</li>
				<li className={classNames(bullet, "flex items-center gap-2")}>
					<Trans
						components={{ b: <b className="font-semibold text-text" /> }}
						i18nKey="leadScoring.overview.configured"
						values={{ count: stats.usage.length }}
					/>
					<button
						className="flex h-6 cursor-pointer items-center gap-1 rounded-md border border-border-light px-2 text-[11px] font-medium text-text hover:bg-background-light"
						data-testid="overview-create-scoring"
						type="button"
						onClick={onCreateScoring}
					>
						<Icon className="h-3 w-3" icon={faPlus} />
						{t("leadScoring.overview.createAdditional")}
					</button>
				</li>
				<li className={bullet}>
					<Trans
						components={{ b: <b className="font-semibold text-text" /> }}
						i18nKey="leadScoring.overview.scored"
						values={{ count: stats.scored }}
					/>
				</li>
				{stats.missing > 0 && (
					<li className={bullet}>
						<span className="inline-flex items-center gap-1.5 rounded-md border border-status-error-light bg-white px-2 py-0.5 text-status-error">
							<Icon className="h-3 w-3" icon={faTriangleExclamation} />
							<Trans
								components={{ b: <b className="font-semibold" /> }}
								i18nKey="leadScoring.overview.missing"
								values={{ count: stats.missing }}
							/>
						</span>
					</li>
				)}
			</ul>
			{stats.usage.length > 0 && <div className="my-3 h-px bg-border-light" />}
			{stats.usage.map((usage, index) => {
				const percent = stats.live ? Math.round((usage.cells / stats.live) * 100) : 0;

				return (
					<div
						key={usage.criteriaId}
						className="group grid grid-cols-[9px_260px_190px_150px_auto] items-center gap-2.5 py-1 text-xs"
						data-testid={`overview-usage-${usage.criteriaId}`}
					>
						<span
							className={classNames(
								"h-2 w-2 rounded-full",
								SCORING_SWATCHES[index % SCORING_SWATCHES.length]
							)}
						/>
						<span className="truncate font-semibold text-text">{usage.name}</span>
						<span className="text-text-meta">
							<Trans
								components={{ b: <b className="font-semibold text-text" /> }}
								i18nKey="leadScoring.overview.appliedTo"
								values={{ count: usage.cells }}
							/>
						</span>
						<span className="h-1.5 w-full overflow-hidden rounded bg-background-light">
							<span
								className={classNames(
									"block h-full",
									SCORING_SWATCHES[index % SCORING_SWATCHES.length]
								)}
								style={{ width: `${percent}%` }}
							/>
						</span>
						<button
							className="invisible flex h-6 w-fit cursor-pointer items-center gap-1 rounded-md border border-border-light px-2 text-[11px] font-medium text-text hover:bg-background-light group-hover:visible"
							data-testid={`overview-edit-${usage.criteriaId}`}
							type="button"
							onClick={() => onEditScoring(usage.criteriaId)}
						>
							<Icon className="h-3 w-3" icon={faSlidersH} />
							{t("leadScoring.matrix.editScoringShort")}
						</button>
					</div>
				);
			})}
		</div>
	);
};
